import { useId } from "react";

const SKIN = { light: "#f6c9a8", mid: "#e0a27a", deep: "#9a6342" };
const INK = "#1d2340";

/**
 * Round 64×64 frame that clips each character to a circle.
 *
 * @param {{bg: string, label: string, children: React.ReactNode}} props
 * @return {JSX.Element}
 */
const AvatarFrame = ({ bg, label, children }) => {
  const clipId = `avatar-${useId().replace(/:/g, "")}`;
  return (
    <svg viewBox="0 0 64 64" role="img" aria-label={label} width="100%" height="100%">
      <defs>
        <clipPath id={clipId}>
          <circle cx="32" cy="32" r="32" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect width="64" height="64" fill={bg} />
        {children}
      </g>
    </svg>
  );
};

const Shoulders = ({ fill, y = 48 }) => (
  <path
    d={`M8 64 C8 ${y + 2} 19 ${y - 4} 32 ${y - 4} C45 ${y - 4} 56 ${y + 2} 56 64 Z`}
    fill={fill}
  />
);

const Eyes = ({ y = 33, gap = 5, r = 1.8, fill = INK }) => (
  <>
    <circle cx={32 - gap} cy={y} r={r} fill={fill} />
    <circle cx={32 + gap} cy={y} r={r} fill={fill} />
  </>
);

const Smile = ({ y = 38, w = 4, stroke = INK }) => (
  <path
    d={`M${32 - w} ${y} Q32 ${y + 3} ${32 + w} ${y}`}
    fill="none"
    stroke={stroke}
    strokeLinecap="round"
    strokeWidth="1.6"
  />
);

const Astronaut = () => (
  <AvatarFrame bg="#7fb3ff" label="Astronaut">
    <Shoulders fill="#f2f4f8" />
    <rect x="27" y="52" width="10" height="6" rx="1.5" fill="#ff6b6b" />
    <circle cx="32" cy="30" r="18" fill="#f2f4f8" stroke="#c9cfdb" strokeWidth="2" />
    <ellipse cx="32" cy="31" rx="12" ry="10" fill={INK} />
    <path
      d="M24 27 Q27 23 32 23"
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeWidth="2"
      opacity="0.6"
    />
    <circle cx="36" cy="33" r="1.4" fill="#8fd3ff" />
  </AvatarFrame>
);

const Wizard = () => (
  <AvatarFrame bg="#ffd166" label="Wizard">
    <Shoulders fill="#4b3fa6" />
    <circle cx="32" cy="35" r="10" fill={SKIN.light} />
    <path d="M22 37 Q32 62 42 37 Q32 44 22 37 Z" fill="#fff" />
    <Eyes y={34} gap={4} r={1.5} />
    <path d="M15 28 L32 3 L49 28 Z" fill="#4b3fa6" />
    <ellipse cx="32" cy="28" rx="19" ry="4" fill="#3a2f8a" />
    <path
      d="M30 13 l1 2.2 2.4.3-1.8 1.6.5 2.4-2.1-1.2-2.1 1.2.5-2.4-1.8-1.6 2.4-.3z"
      fill="#ffd166"
    />
    <circle cx="38" cy="21" r="1.2" fill="#ffd166" />
  </AvatarFrame>
);

const Robot = () => (
  <AvatarFrame bg="#6c8cff" label="Robot">
    <Shoulders fill="#9aa3b5" />
    <line x1="32" y1="9" x2="32" y2="16" stroke="#9aa3b5" strokeWidth="2" />
    <circle cx="32" cy="8" r="3" fill="#ff6b6b" />
    <rect x="15" y="16" width="34" height="28" rx="8" fill="#dfe4ee" />
    <rect x="12" y="25" width="4" height="10" rx="2" fill="#9aa3b5" />
    <rect x="48" y="25" width="4" height="10" rx="2" fill="#9aa3b5" />
    <rect x="20" y="22" width="24" height="15" rx="5" fill={INK} />
    <circle cx="27" cy="29" r="2.6" fill="#5ef0ff" />
    <circle cx="37" cy="29" r="2.6" fill="#5ef0ff" />
    <path d="M28 33.5 h8" stroke="#5ef0ff" strokeLinecap="round" strokeWidth="1.4" />
  </AvatarFrame>
);

const Lion = () => (
  <AvatarFrame bg="#ffb347" label="Lion">
    <Shoulders fill="#b85c1c" />
    <circle cx="32" cy="31" r="21" fill="#b85c1c" />
    <circle cx="21" cy="19" r="4" fill="#f3b35c" />
    <circle cx="43" cy="19" r="4" fill="#f3b35c" />
    <circle cx="32" cy="32" r="14" fill="#f3b35c" />
    <ellipse cx="32" cy="38" rx="7" ry="5" fill="#ffe1a8" />
    <Eyes y={30} gap={5} r={1.7} />
    <path d="M29.5 35 h5 l-2.5 3 z" fill="#6b3a1a" />
    <path
      d="M32 38 v1.5 M29 40.5 q3 2 6 0"
      fill="none"
      stroke="#6b3a1a"
      strokeLinecap="round"
      strokeWidth="1.2"
    />
  </AvatarFrame>
);

const Pirate = () => (
  <AvatarFrame bg="#4dc3c3" label="Pirate">
    <Shoulders fill="#2b2b3a" />
    <circle cx="32" cy="35" r="11" fill={SKIN.mid} />
    <path d="M21 37 Q32 56 43 37 Q32 43 21 37 Z" fill="#6b3a1a" />
    <circle cx="36.5" cy="33" r="1.7" fill={INK} />
    <ellipse cx="27" cy="33" rx="3.4" ry="2.8" fill={INK} />
    <path d="M21 30 L43 27" stroke={INK} strokeWidth="1.2" />
    <path d="M12 26 Q32 6 52 26 Q46 30 32 25 Q18 30 12 26 Z" fill="#1f1f2b" />
    <circle cx="32" cy="17" r="2.6" fill="#fff" />
    <path d="M29 21 l6 -2 M29 19 l6 2" stroke="#fff" strokeWidth="1" />
  </AvatarFrame>
);

const Monster = () => (
  <AvatarFrame bg="#ffe066" label="Monster">
    <path d="M10 64 C8 40 14 20 32 20 C50 20 56 40 54 64 Z" fill="#d36bd9" />
    <path d="M20 22 l3 -7 3 6 3 -8 3 8 3 -7 3 7 3 -6 2 7" fill="#d36bd9" />
    <circle cx="32" cy="34" r="8" fill="#fff" />
    <circle cx="33" cy="35" r="4" fill={INK} />
    <circle cx="34.5" cy="33.5" r="1.2" fill="#fff" />
    <path d="M24 46 Q32 53 40 46 Z" fill={INK} />
    <path d="M27 46 v2.5 h2.5 v-2.5 M35 46 v2.5 h2.5 v-2.5" fill="#fff" />
  </AvatarFrame>
);

const Panda = () => (
  <AvatarFrame bg="#8fd694" label="Panda">
    <Shoulders fill="#2b2b3a" />
    <circle cx="19" cy="19" r="6" fill="#2b2b3a" />
    <circle cx="45" cy="19" r="6" fill="#2b2b3a" />
    <circle cx="32" cy="32" r="17" fill="#fff" />
    <ellipse cx="25" cy="31" rx="4.5" ry="5.5" transform="rotate(-25 25 31)" fill="#2b2b3a" />
    <ellipse cx="39" cy="31" rx="4.5" ry="5.5" transform="rotate(25 39 31)" fill="#2b2b3a" />
    <circle cx="25.5" cy="31" r="1.5" fill="#fff" />
    <circle cx="38.5" cy="31" r="1.5" fill="#fff" />
    <ellipse cx="32" cy="38" rx="2.6" ry="1.8" fill="#2b2b3a" />
    <Smile y={40.5} w={3} />
  </AvatarFrame>
);

const Gnome = () => (
  <AvatarFrame bg="#ffc4c4" label="Gnome">
    <Shoulders fill="#3f9e5a" />
    <circle cx="32" cy="34" r="9" fill={SKIN.light} />
    <path d="M20 35 Q32 64 44 35 Q32 42 20 35 Z" fill="#fff" />
    <circle cx="32" cy="36" r="3" fill="#f08a8a" />
    <Eyes y={32} gap={4} r={1.4} />
    <path d="M19 31 L32 2 L45 31 Z" fill="#e63946" />
    <ellipse cx="32" cy="31" rx="14" ry="3" fill="#c92f3b" />
  </AvatarFrame>
);

const Alien = () => (
  <AvatarFrame bg="#2f3b52" label="Alien">
    <Shoulders fill="#4caf50" />
    <ellipse cx="32" cy="31" rx="16" ry="19" fill="#7ed957" />
    <ellipse cx="25" cy="31" rx="5" ry="3" transform="rotate(30 25 31)" fill={INK} />
    <ellipse cx="39" cy="31" rx="5" ry="3" transform="rotate(-30 39 31)" fill={INK} />
    <circle cx="24" cy="30" r="1" fill="#fff" />
    <circle cx="38" cy="30" r="1" fill="#fff" />
    <path d="M29 42 q3 1.5 6 0" fill="none" stroke={INK} strokeLinecap="round" strokeWidth="1.4" />
    <circle cx="14" cy="10" r="1" fill="#fff" />
    <circle cx="52" cy="14" r="1.3" fill="#fff" />
  </AvatarFrame>
);

const Fox = () => (
  <AvatarFrame bg="#ffe3c2" label="Fox">
    <Shoulders fill="#e8702a" />
    <path d="M15 14 L25 24 L18 32 Z M49 14 L39 24 L46 32 Z" fill="#e8702a" />
    <path d="M17 17 L23 24 L19 28 Z M47 17 L41 24 L45 28 Z" fill="#2b2b3a" />
    <path d="M16 28 Q32 14 48 28 Q46 44 32 50 Q18 44 16 28 Z" fill="#f28c38" />
    <path d="M20 34 Q26 36 32 50 Q38 36 44 34 Q40 46 32 50 Q24 46 20 34 Z" fill="#fff" />
    <Eyes y={32} gap={6} r={1.8} />
    <ellipse cx="32" cy="44" rx="2.4" ry="1.8" fill="#2b2b3a" />
  </AvatarFrame>
);

const Frog = () => (
  <AvatarFrame bg="#a8e6ff" label="Frog">
    <Shoulders fill="#4c9a2a" />
    <circle cx="22" cy="24" r="7" fill="#6cc04a" />
    <circle cx="42" cy="24" r="7" fill="#6cc04a" />
    <ellipse cx="32" cy="38" rx="20" ry="14" fill="#6cc04a" />
    <circle cx="22" cy="24" r="4" fill="#fff" />
    <circle cx="42" cy="24" r="4" fill="#fff" />
    <circle cx="22.5" cy="24.5" r="2" fill={INK} />
    <circle cx="42.5" cy="24.5" r="2" fill={INK} />
    <path
      d="M20 40 Q32 48 44 40"
      fill="none"
      stroke={INK}
      strokeLinecap="round"
      strokeWidth="1.8"
    />
    <circle cx="18" cy="38" r="2.5" fill="#ff9ab0" opacity="0.8" />
    <circle cx="46" cy="38" r="2.5" fill="#ff9ab0" opacity="0.8" />
  </AvatarFrame>
);

const Diver = () => (
  <AvatarFrame bg="#4f6bed" label="Deep-sea diver">
    <Shoulders fill="#a8741a" />
    <circle cx="32" cy="32" r="20" fill="#d9a441" stroke="#a8741a" strokeWidth="2" />
    <circle cx="32" cy="32" r="11" fill="#8fd3ff" stroke="#a8741a" strokeWidth="3" />
    <path
      d="M26 28 Q28 25 31 25"
      fill="none"
      stroke="#fff"
      strokeLinecap="round"
      strokeWidth="2"
      opacity="0.8"
    />
    <circle cx="32" cy="15" r="1.6" fill="#a8741a" />
    <circle cx="32" cy="49" r="1.6" fill="#a8741a" />
    <circle cx="15" cy="32" r="1.6" fill="#a8741a" />
    <circle cx="49" cy="32" r="1.6" fill="#a8741a" />
  </AvatarFrame>
);

const Knight = () => (
  <AvatarFrame bg="#ffd8a8" label="Knight">
    <Shoulders fill="#8a93a6" />
    <path d="M30 8 Q42 4 40 16 Q36 10 30 14 Z" fill="#e63946" />
    <path d="M16 50 V30 Q16 14 32 14 Q48 14 48 30 V50 Z" fill="#c3cad6" />
    <path d="M32 14 V50" stroke="#a3abba" strokeWidth="1.5" />
    <rect x="20" y="28" width="24" height="3" rx="1.5" fill={INK} />
    <rect x="20" y="34" width="24" height="3" rx="1.5" fill={INK} />
    <circle cx="24" cy="43" r="1" fill="#8a93a6" />
    <circle cx="40" cy="43" r="1" fill="#8a93a6" />
  </AvatarFrame>
);

const Mummy = () => (
  <AvatarFrame bg="#ffd43b" label="Mummy">
    <Shoulders fill="#e9dcc3" />
    <circle cx="32" cy="32" r="17" fill="#f1e6cf" />
    <rect x="15" y="28" width="34" height="8" fill="#3a2f2a" />
    <circle cx="26" cy="32" r="2.4" fill="#fff" />
    <circle cx="38" cy="32" r="2.4" fill="#fff" />
    <circle cx="26.5" cy="32.3" r="1.1" fill={INK} />
    <circle cx="38.5" cy="32.3" r="1.1" fill={INK} />
    <g stroke="#cdbb98" strokeLinecap="round" strokeWidth="1.4">
      <path d="M17 22 L46 18" />
      <path d="M16 26 L44 24" />
      <path d="M17 40 L47 38" />
      <path d="M19 45 L43 46" />
    </g>
  </AvatarFrame>
);

const Cat = () => (
  <AvatarFrame bg="#b5e48c" label="Cat">
    <Shoulders fill="#6b7280" />
    <path d="M16 14 L26 22 L17 30 Z M48 14 L38 22 L47 30 Z" fill="#9ca3af" />
    <path d="M18.5 18 L24 22.5 L19 27 Z M45.5 18 L40 22.5 L45 27 Z" fill="#f9a8d4" />
    <circle cx="32" cy="33" r="16" fill="#9ca3af" />
    <ellipse cx="26" cy="31" rx="2.6" ry="3.2" fill="#a3e635" />
    <ellipse cx="38" cy="31" rx="2.6" ry="3.2" fill="#a3e635" />
    <ellipse cx="26" cy="31" rx="0.9" ry="2.4" fill={INK} />
    <ellipse cx="38" cy="31" rx="0.9" ry="2.4" fill={INK} />
    <path d="M30.5 37 h3 l-1.5 1.8 z" fill="#f472b6" />
    <g stroke="#e5e7eb" strokeLinecap="round" strokeWidth="1">
      <path d="M22 38 L13 36 M22 40 L13 41" />
      <path d="M42 38 L51 36 M42 40 L51 41" />
    </g>
  </AvatarFrame>
);

const Flame = () => (
  <AvatarFrame bg="#2b2d42" label="Flame">
    <path
      d="M32 8 C36 18 46 20 46 36 C46 47 40 54 32 54 C24 54 18 47 18 36 C18 28 23 25 24 18 C28 22 28 26 29 28 C30 20 31 14 32 8 Z"
      fill="#ff7a29"
    />
    <path
      d="M32 26 C35 32 40 34 40 41 C40 47 36 50 32 50 C28 50 24 47 24 41 C24 36 28 34 32 26 Z"
      fill="#ffd166"
    />
    <Eyes y={40} gap={4} r={1.6} />
    <Smile y={44} w={2.5} />
    <circle cx="13" cy="50" r="1.5" fill="#ff7a29" opacity="0.7" />
    <circle cx="51" cy="20" r="1.2" fill="#ffd166" opacity="0.7" />
  </AvatarFrame>
);

/**
 * System avatars in picker order. Keys match ``UserAvatarChoices`` on the API.
 *
 * @const {!Array<{key: string, label: string, Component: function(): JSX.Element}>}
 */
export const SYSTEM_AVATARS = [
  { key: "astronaut", label: "Astronaut", Component: Astronaut },
  { key: "wizard", label: "Wizard", Component: Wizard },
  { key: "robot", label: "Robot", Component: Robot },
  { key: "lion", label: "Lion", Component: Lion },
  { key: "pirate", label: "Pirate", Component: Pirate },
  { key: "monster", label: "Monster", Component: Monster },
  { key: "panda", label: "Panda", Component: Panda },
  { key: "gnome", label: "Gnome", Component: Gnome },
  { key: "alien", label: "Alien", Component: Alien },
  { key: "fox", label: "Fox", Component: Fox },
  { key: "frog", label: "Frog", Component: Frog },
  { key: "diver", label: "Diver", Component: Diver },
  { key: "knight", label: "Knight", Component: Knight },
  { key: "mummy", label: "Mummy", Component: Mummy },
  { key: "cat", label: "Cat", Component: Cat },
  { key: "flame", label: "Flame", Component: Flame },
];

/** @const {!Object<string, function(): JSX.Element>} */
export const SYSTEM_AVATAR_BY_KEY = Object.fromEntries(
  SYSTEM_AVATARS.map(({ key, Component }) => [key, Component]),
);
